import { Suspense, lazy, useEffect, useRef, useState } from "react";
import { BrowserRouter, Routes, Route } from "react-router";
import { SignedIn, SignedOut, useAuth } from "@clerk/clerk-react";
import { Helmet } from "react-helmet-async";
import Intercom, {
  update as intercomUpdate,
  shutdown as intercomShutdown,
} from "@intercom/messenger-js-sdk";
import ls from "@livesession/browser";
import { useGetCurrentUserQuery, useGetIntercomTokenQuery } from "./api/users";
import { useGetUserAttorneyQuery } from "./api/attorneys";
import type { Attorney } from "../../api/src/types";
import { skipToken } from "@reduxjs/toolkit/query/react";
import { PageLoading } from "./components/Loading";
import ErrorMessage, { PageErrorContainer } from "./components/Error";
import { formatUserFullName } from "./utils/format";
import config, { AppType, Environment } from "./config";
import LoadToError from "./LoadToError";
import { RudderStackTracker } from "./components/RudderStackTracker";
import { identifyUser, trackEvent, analytics } from "./utils/rudderstack";

// Re-mint the Intercom session JWT this long before it expires, so a
// long-lived (always-open) tab keeps an identity-verified Messenger session
// without ever hitting an expired token.
const INTERCOM_TOKEN_REFRESH_BUFFER_MS = 5 * 60 * 1000; // 5 minutes

// If a scheduled refresh fails, retry on this cadence instead of giving up; a
// one-shot timer would otherwise leave a long-lived tab stuck on an expiring
// token until the page is reloaded.
const INTERCOM_TOKEN_REFRESH_RETRY_MS = 30 * 1000; // 30 seconds

// Lazy-load entire route modules per user type.
// Each module contains its own layout, lazy page imports, and route definitions,
// so a customer never downloads admin/attorney code and vice versa.
const CustomerRoutes = lazy(() => import("./routes/customerRoutes"));
const AttorneyRoutes = lazy(() => import("./routes/attorneyRoutes"));
const AdminRoutes = lazy(() => import("./routes/adminRoutes"));

// Auth pages are small and shared across all unauthenticated users
const SigninRedirect = lazy(() => import("./pages/auth/SigninRedirect"));
const Login = lazy(() => import("./pages/auth/Signin"));
const Signup = lazy(() => import("./pages/auth/Signup"));
const OAuthCallback = lazy(() => import("./pages/auth/OAuthCallback"));

function SuspenseRoute({ children }: { children: React.ReactNode }) {
  return <Suspense fallback={<PageLoading />}>{children}</Suspense>;
}

function SignedInRoutes({
  attorney,
  isUserAttorney,
  isAdmin,
  isCustomer,
  isUserFetching,
}: {
  attorney: Attorney | undefined;
  isUserAttorney: boolean;
  isAdmin: boolean;
  isCustomer: boolean;
  isUserFetching: boolean;
}) {
  if (isAdmin) {
    return (
      <SuspenseRoute>
        <AdminRoutes />
      </SuspenseRoute>
    );
  }

  if (isUserAttorney && attorney) {
    return (
      <SuspenseRoute>
        <AttorneyRoutes attorney={attorney} />
      </SuspenseRoute>
    );
  }

  if (isCustomer) {
    return (
      <SuspenseRoute>
        <CustomerRoutes />
      </SuspenseRoute>
    );
  }

  return (
    <Routes>
      <Route
        path="/oauth/callback"
        element={
          <PageErrorContainer>
            <SuspenseRoute>
              <OAuthCallback />
            </SuspenseRoute>
          </PageErrorContainer>
        }
      />
      <Route path="*" element={<LoadToError isFetching={isUserFetching} />} />
    </Routes>
  );
}

function NoDbUserRoutes({ isUserFetching }: { isUserFetching: boolean }) {
  return (
    <Routes>
      <Route
        path="/login"
        element={
          <SuspenseRoute>
            <Login />
          </SuspenseRoute>
        }
      />
      <Route
        path="/signup"
        element={
          <SuspenseRoute>
            <Signup />
          </SuspenseRoute>
        }
      />
      <Route
        path="/oauth/callback"
        element={
          <PageErrorContainer>
            <SuspenseRoute>
              <OAuthCallback />
            </SuspenseRoute>
          </PageErrorContainer>
        }
      />
      <Route
        path="*"
        element={
          <>
            <SignedIn>
              <LoadToError isFetching={isUserFetching} />
            </SignedIn>
            <SignedOut>
              <SuspenseRoute>
                <SigninRedirect />
              </SuspenseRoute>
            </SignedOut>
          </>
        }
      />
    </Routes>
  );
}

function App() {
  const { isSignedIn } = useAuth();
  const {
    data: user,
    isLoading: isUserLoading,
    isFetching: isUserFetching,
  } = useGetCurrentUserQuery();
  const hasSignedInDbUser = Boolean(user && isSignedIn);
  const isUserAttorney = user?.userType === "ATTORNEY";
  const isAdmin = user?.userType === "ADMIN";
  const isCustomer = user?.userType === "CUSTOMER";
  const { data: attorney, isLoading: isAttorneyLoading } =
    useGetUserAttorneyQuery(
      user && isSignedIn && isUserAttorney ? user.id : skipToken,
    );

  // Token-based session for Intercom identity verification. Only fetched once
  // we have a signed-in database user, since the JWT is minted for that user.
  // Keyed by user id so a previous account's token can never be reused.
  const { data: intercomData, refetch: refetchIntercomToken } =
    useGetIntercomTokenQuery(hasSignedInDbUser && user ? user.id : skipToken);
  const intercomUserJwt = intercomData?.token;
  const intercomTokenExpiresAt = intercomData?.expiresAt;

  const lastIdentifiedUserId = useRef<string | null>(null);
  // Tracks whether the Messenger has been booted, so token refreshes update
  // the existing session in place instead of re-booting it.
  const intercomBootedRef = useRef(false);
  // Bumped to force the refresh scheduler to re-run and retry after a failed
  // token refresh (see the scheduling effect below).
  const [intercomRefreshNonce, setIntercomRefreshNonce] = useState(0);

  useEffect(() => {
    if (user && hasSignedInDbUser) {
      if (config.environment !== Environment.DEVELOPMENT) {
        ls.identify({
          name: formatUserFullName(user, "full"),
          email: user.email,
        });
      }

      if (lastIdentifiedUserId.current !== user.id) {
        identifyUser(user.id, {
          email: user.email,
          user_type: user.userType,
          created_at: user.createdAt,
        });
        trackEvent("user_identified", {
          user_id: user.id,
          user_type: user.userType,
        });
        lastIdentifiedUserId.current = user.id;
      }
    } else {
      if (lastIdentifiedUserId.current) {
        analytics.reset();
        lastIdentifiedUserId.current = null;
      }
    }
  }, [hasSignedInDbUser, user]);

  // Boot the identity-verified Messenger once we have a session JWT, and keep
  // it verified across token refreshes (see the scheduling effect below).
  useEffect(() => {
    if (user && hasSignedInDbUser && intercomUserJwt) {
      if (intercomBootedRef.current) {
        // A refreshed token: update the live session in place so the open
        // Messenger (e.g. an active conversation) isn't torn down. Re-send the
        // display fields too so profile changes (e.g. a renamed user) aren't
        // left stale until the next full re-boot.
        intercomUpdate({
          intercom_user_jwt: intercomUserJwt,
          name: formatUserFullName(user, "full"),
          created_at: new Date(user.createdAt).getTime() / 1000,
        });
      } else {
        Intercom({
          app_id: config.intercomAppId,
          // Token-based session: the signed JWT carries the verified identity
          // (user_id + email). The remaining fields are display attributes.
          intercom_user_jwt: intercomUserJwt,
          name: formatUserFullName(user, "full"),
          created_at: new Date(user.createdAt).getTime() / 1000,
        });
        intercomBootedRef.current = true;
      }
    } else if (!hasSignedInDbUser && intercomBootedRef.current) {
      // Signed out: tear down the verified session so the next user starts
      // fresh rather than inheriting the previous identity.
      intercomShutdown();
      intercomBootedRef.current = false;
    }
  }, [hasSignedInDbUser, user, intercomUserJwt]);

  // Re-mint the session JWT shortly before it expires so always-open tabs
  // never run with an expired token. A successful refresh changes
  // `expiresAt`, which re-runs this effect and schedules the next refresh; a
  // failed refresh changes nothing, so we bump a nonce to retry rather than
  // silently giving up.
  useEffect(() => {
    if (!hasSignedInDbUser || !intercomTokenExpiresAt) {
      return;
    }
    let active = true;
    let retryTimer: ReturnType<typeof setTimeout> | undefined;
    const msUntilRefresh =
      intercomTokenExpiresAt * 1000 -
      Date.now() -
      INTERCOM_TOKEN_REFRESH_BUFFER_MS;
    const refreshTimer = setTimeout(
      () => {
        refetchIntercomToken()
          .unwrap()
          .catch(() => {
            if (!active) {
              return;
            }
            retryTimer = setTimeout(() => {
              if (active) {
                setIntercomRefreshNonce((nonce) => nonce + 1);
              }
            }, INTERCOM_TOKEN_REFRESH_RETRY_MS);
          });
      },
      Math.max(msUntilRefresh, 0),
    );
    return () => {
      active = false;
      clearTimeout(refreshTimer);
      if (retryTimer) {
        clearTimeout(retryTimer);
      }
    };
  }, [
    hasSignedInDbUser,
    intercomTokenExpiresAt,
    refetchIntercomToken,
    intercomRefreshNonce,
  ]);

  if (isUserLoading || isAttorneyLoading) {
    return <PageLoading />;
  }

  if (hasSignedInDbUser && isUserAttorney && !attorney) {
    return (
      <PageErrorContainer>
        <ErrorMessage>No attorney found</ErrorMessage>
      </PageErrorContainer>
    );
  }

  const isPostnup = config.appType === AppType.POSTNUP;

  return (
    <BrowserRouter>
      <Helmet>
        <title>
          {isPostnup
            ? "Postnup.com - Online Postnuptial Agreements"
            : "HelloPrenup - Online Prenuptial Agreements | Affordable Prenups"}
        </title>
        <meta
          name="description"
          content={
            isPostnup
              ? "Create your postnuptial agreement online with Postnup.com. Affordable, legally valid postnups created by legal professionals."
              : "Create your prenuptial agreement online with HelloPrenup. Affordable, legally valid prenups created by legal professionals."
          }
        />
      </Helmet>
      <RudderStackTracker />
      {hasSignedInDbUser ? (
        <SignedInRoutes
          attorney={attorney}
          isUserAttorney={isUserAttorney}
          isAdmin={isAdmin}
          isCustomer={isCustomer}
          isUserFetching={isUserFetching}
        />
      ) : (
        // No authenticated DB user for the current Clerk session: either signed
        // out, or signed in but still onboarding (e.g. mid-signup/login,
        // including cross-brand email verification). These routes are not gated
        // only on Clerk's signed-in state so setActive() during signup/login
        // does not unmount the auth flow before email verification can finish.
        <NoDbUserRoutes isUserFetching={isUserFetching} />
      )}
    </BrowserRouter>
  );
}

export default App;
